import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import {
  QUIET_PROGRESS_OPTIONS,
  onboardingStage,
  useHideGettingStarted,
  useOnboardingProgress,
} from "@/hooks/useOnboardingProgress";
import useIsMobile from "@/hooks/useIsMobile";
import { GUIDE_CLOSED_EVENT, guideIsShowing } from "@/components/GuidedSpotlight";
import { SETUP_GUIDE_OPEN_EVENT, copyForStep, orderForFocus, runStepAction, startMemberTour } from "./steps";
import { getUserId, getUserType } from "@/helpers/common";
import { readTourFocus, readTourResume } from "@/components/MemberTour/store";
import { USER_TYPE_AGENT } from "@/constants/common";
import "./SetupGuide.scss";

const storeKey = (userId) => `onboardingDoneSteps:${userId}`;

const readDone = (userId) => {
  try {
    const value = JSON.parse(localStorage.getItem(storeKey(userId)) || "null");
    return Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
};

const writeDone = (userId, ids) => {
  try {
    localStorage.setItem(storeKey(userId), JSON.stringify(ids));
  } catch {}
};

const pad = (n) => String(n).padStart(2, "0");

// Local calendar day: a UTC day would roll over mid-evening for US agents.
export const dayOf = (date = new Date()) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const shouldWelcomeBack = (userId, stage, tourSeen, today = dayOf()) => {
  if (stage !== "new" || !tourSeen) return false;
  try {
    const firstKey = `setupGuide:firstSeen:${userId}`;
    const nudgeKey = `setupGuide:welcomeBack:${userId}`;
    const firstSeen = localStorage.getItem(firstKey);
    if (!firstSeen) {
      localStorage.setItem(firstKey, today);
      return false;
    }
    if (firstSeen >= today || localStorage.getItem(nudgeKey) === today) return false;
    localStorage.setItem(nudgeKey, today);
    return true;
  } catch {
    return false;
  }
};

export const freshlyDone = (previous, steps) => {
  if (!previous) return null;
  const fresh = steps.filter((step) => step.done && !previous.includes(step.id));
  return fresh.length ? fresh[fresh.length - 1] : null;
};

const FORM_ROUTES = [
  pageURLs.login,
  pageURLs.signup,
  pageURLs.createwantad,
  pageURLs.agentCreatewantadMainScreen,
  pageURLs.agentCreatewantad,
  pageURLs.agentEditwantad,
  pageURLs.editWantAdForAgents,
  pageURLs.memberCreateInventory,
  pageURLs.memberEditInventory,
  pageURLs.inventoryStrengthen,
  pageURLs.selectYourPlan,
  pageURLs.completeYourProfilePremium,
  pageURLs.completeYourProfileDefault,
  pageURLs.addNewCard,
  pageURLs.gitchaMemberWelcome,
  pageURLs.gitchaInvetoryOnBoard,
  pageURLs.buyerListingConfirmation,
  "/form",
  "/user-type",
  "/edit-agent-profile",
  "/enter-passcode",
].map((route) => route.toLowerCase());

export const isFormRoute = (pathname = "") => {
  const path = pathname.toLowerCase().replace(/\/+$/, "");
  return FORM_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
};

const OVERLAYS = ".modal.show, .offcanvas.show, .guided-spotlight, .mobile-bottom-nav-overlay";
const FULL_SCREEN_ROUTES = [pageURLs.agentChat];
const NAV_BAR = ".mobile-bottom-nav-bar";

export const isFullScreenRoute = (pathname = "") => {
  const path = pathname.toLowerCase().replace(/\/+$/, "");
  return FULL_SCREEN_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
};

export const useOverlayOpen = (active) => {
  const [covered, setCovered] = useState(false);
  useEffect(() => {
    if (!active) return undefined;
    let frame = 0;
    const check = () => {
      frame = 0;
      setCovered(Boolean(document.querySelector(OVERLAYS)));
    };
    check();
    const observer = new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(check);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [active]);
  return active && covered;
};

export const useDockAboveNav = (active, rootRef) => {
  useEffect(() => {
    const root = rootRef.current;
    if (!active || !root) return undefined;
    document.body.classList.add("has-setup-band");
    const nav = document.querySelector(NAV_BAR);
    const sync = () => root.style.setProperty("--setup-band-bottom", `${nav ? Math.round(nav.getBoundingClientRect().height) : 0}px`);
    sync();
    const observer = nav && typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    if (observer) observer.observe(nav);
    window.addEventListener("resize", sync);
    return () => {
      document.body.classList.remove("has-setup-band");
      window.removeEventListener("resize", sync);
      if (observer) observer.disconnect();
    };
  }, [active, rootRef]);
};

const Check = ({ size = 12 }) => (
  <svg viewBox="0 0 16 16" width={size} height={size} aria-hidden="true">
    <path
      d="M3.5 8.5l3 3 6-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Ring = ({ done, total, size = 24 }) => {
  const center = size / 2;
  const radius = center - 3;
  const circumference = 2 * Math.PI * radius;
  const share = total ? done / total : 0;
  return (
    <svg
      className="setup-guide__ring"
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      aria-hidden="true"
    >
      <circle cx={center} cy={center} r={radius} className="setup-guide__ring-track" />
      <circle
        cx={center}
        cy={center}
        r={radius}
        className="setup-guide__ring-fill"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - share)}
      />
    </svg>
  );
};

const SetupGuide = ({ placement = "floating", collapsed = false }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const isMobile = useIsMobile();
  const inSidebar = placement === "sidebar";
  const userId = getUserId();
  const enabled =
    Boolean(userId) &&
    !isGitchaDefaultWebsite &&
    Number(getUserType()) === USER_TYPE_AGENT &&
    inSidebar !== isMobile;
  const { data: progress } = useOnboardingProgress(enabled, QUIET_PROGRESS_OPTIONS);
  const hide = useHideGettingStarted();
  const [open, setOpen] = useState(false);
  const resume = open ? readTourResume(userId) : null;
  const [justDone, setJustDone] = useState(null);
  const [welcomeBack, setWelcomeBack] = useState(false);
  const rootRef = useRef(null);
  const stage = onboardingStage(progress);
  const onForm = isFormRoute(pathname);
  const pillWanted =
    enabled &&
    !["unknown", "hidden", "done"].includes(stage) &&
    (inSidebar || (!onForm && !isFullScreenRoute(pathname)));
  const covered = useOverlayOpen(pillWanted && !inSidebar);
  useDockAboveNav(pillWanted && !inSidebar && !covered, rootRef);

  const pendingDoneRef = useRef(null);

  const close = () => {
    setOpen(false);
    setJustDone(null);
    setWelcomeBack(false);
  };

  useEffect(() => {
    if (!enabled || !progress || onForm) return;
    const steps = progress.steps.filter((step) => copyForStep(step));
    const previous = readDone(userId);
    writeDone(userId, steps.filter((step) => step.done).map((step) => step.id));
    if (onboardingStage(progress) === "hidden") return;
    if (document.body.classList.contains("member-tour-open")) return;
    const done = freshlyDone(previous, steps);
    if (done && guideIsShowing()) {
      pendingDoneRef.current = done;
      return;
    }
    if (done) {
      setJustDone(done);
      setOpen(true);
      return;
    }
    if (shouldWelcomeBack(userId, onboardingStage(progress), progress.tourSeen)) {
      setWelcomeBack(true);
      setOpen(true);
    }
  }, [enabled, progress, userId, onForm]);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(SETUP_GUIDE_OPEN_EVENT, show);
    return () => window.removeEventListener(SETUP_GUIDE_OPEN_EVENT, show);
  }, []);

  useEffect(() => {
    const celebrate = () => {
      const done = pendingDoneRef.current;
      if (!done) return;
      pendingDoneRef.current = null;
      setJustDone(done);
      setOpen(true);
    };
    window.addEventListener(GUIDE_CLOSED_EVENT, celebrate);
    return () => window.removeEventListener(GUIDE_CLOSED_EVENT, celebrate);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && close();
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  if (!enabled || stage === "unknown" || stage === "hidden") return null;
  if (stage === "done" && !open && !inSidebar) return null;

  const steps = orderForFocus(
    progress.steps.filter((step) => copyForStep(step)),
    readTourFocus(userId),
  );
  const doneCount = steps.filter((step) => step.done).length;
  const next = steps.find((step) => !step.done);
  const showPill = (pillWanted || (inSidebar && stage === "done")) && !covered;

  if (!showPill && !open) return null;

  const go = (action) => {
    close();
    runStepAction(action, navigate);
  };

  return (
    <div
      className={`setup-guide${inSidebar ? " is-sidebar" : ""}${inSidebar && collapsed ? " is-collapsed" : ""}`}
      ref={rootRef}
    >
      {open ? (
        <section className="setup-guide__panel" role="dialog" aria-label="Getting started">
          {welcomeBack && !justDone ? (
            <p className="setup-guide__done-line" role="status">
              Welcome back. Pick up where you left off: your next step is&nbsp;below.
            </p>
          ) : null}
          {justDone ? (
            <p className="setup-guide__done-line" role="status">
              <span className="setup-guide__done-mark">
                <Check />
              </span>
              {copyForStep(justDone)?.doneLine || "Step done."}
            </p>
          ) : null}
          <header className="setup-guide__head">
            <div>
              <h3>{stage === "done" ? "You're all set up" : "Getting started"}</h3>
              <p>
                {stage === "done"
                  ? "New matches and introductions come to you as they happen."
                  : `${doneCount} of ${steps.length} done. It stays here until every step is done.`}
              </p>
            </div>
            <button
              type="button"
              className="setup-guide__close"
              aria-label="Close"
              onClick={close}
            >
              ×
            </button>
          </header>
          <div
            className="setup-guide__bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={steps.length}
            aria-valuenow={doneCount}
            aria-label="Steps done"
          >
            <span style={{ width: `${steps.length ? (doneCount / steps.length) * 100 : 0}%` }} />
          </div>
          <ol className="setup-guide__steps">
            {steps.map((step, index) => {
              const copy = copyForStep(step);
              const action = step.done ? null : copy.action(step);
              const isNext = step.id === next?.id;
              return (
                <li
                  key={step.id}
                  className={`setup-guide__step${step.done ? " is-done" : ""}${isNext ? " is-next" : ""}`}
                >
                  <span className="setup-guide__mark">{step.done ? <Check /> : index + 1}</span>
                  <div className="setup-guide__text">
                    <h4>{copy.title}</h4>
                    {isNext ? <p>{copy.outcome}</p> : null}
                  </div>
                  {step.done ? null : action?.note ? (
                    <span className="setup-guide__state">{action.note}</span>
                  ) : action ? (
                    <button
                      type="button"
                      className={`btn ${isNext ? "btn-primary" : "btn-outline-primary"} setup-guide__action`}
                      onClick={() => go(action)}
                    >
                      {action.label}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ol>
          <footer className="setup-guide__foot">
            {stage === "done" ? (
              <button
                type="button"
                className="setup-guide__hide"
                disabled={hide.isPending}
                onClick={() => {
                  close();
                  hide.mutate(progress.tutorialPage);
                }}
              >
                Done
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="setup-guide__replay"
                  onClick={() => {
                    close();
                    startMemberTour({ resume: Boolean(resume) });
                  }}
                >
                  {resume
                    ? `Resume the tour${resume.section ? `: ${resume.section}` : ""}`
                    : "Replay the tour"}
                </button>
                <button type="button" className="setup-guide__hide" onClick={close}>
                  Minimize
                </button>
              </>
            )}
          </footer>
        </section>
      ) : null}
      {showPill && inSidebar ? (
        <button
          type="button"
          className={`setup-guide__card${open ? " is-open" : ""}${stage === "done" ? " is-done" : ""}${stage === "new" ? " is-new" : ""}`}
          title={next ? `Next: ${copyForStep(next)?.title.replace(/\u00a0/g, " ")}` : "Every step is done"}
          aria-expanded={open}
          aria-label={`Getting started, ${doneCount} of ${steps.length} done`}
          onClick={() => (open ? close() : setOpen(true))}
        >
          <span className="setup-guide__card-ring">
            <Ring done={doneCount} total={steps.length} size={42} />
            <span className="setup-guide__card-count">
              {doneCount}/{steps.length}
            </span>
          </span>
          {collapsed ? null : (
            <span className="setup-guide__card-label">{stage === "done" ? "All set" : "Get started"}</span>
          )}
        </button>
      ) : null}
      {showPill && !inSidebar ? (
        <button
          type="button"
          className={`setup-guide__pill${open ? " is-open" : ""}`}
          aria-expanded={open}
          aria-label={`Getting started, ${doneCount} of ${steps.length} done`}
          onClick={() => (open ? close() : setOpen(true))}
        >
          <Ring done={doneCount} total={steps.length} size={24} />
          <span className="setup-guide__pill-label">Getting started</span>
          <span className="setup-guide__pill-count">
            {doneCount}/{steps.length}
          </span>
        </button>
      ) : null}
    </div>
  );
};

export default SetupGuide;
